import React, { useEffect, useMemo, useState } from 'react';
import { LuCheck, LuMapPin, LuPackage, LuSearch, LuTruck, LuX } from 'react-icons/lu';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import BranchAssignmentField, { useBranchAssignment } from '../components/BranchAssignmentField';
import { branchQuerySuffix } from '../components/OverviewCommandPanel';
import { getDistrictNameResolved, getRegionName } from '../utils/tanzaniaLocations';
import './LowStockOrderForm.css';
import './FactoryMaterialOrderForm.css';

const PART_PAGE_SIZE = 30;

/**
 * Spare parts purchase orders from the parts catalog.
 * Stock on hand is updated only when the owner receives the PO (not on submit).
 */
export default function AutomotivePurchaseOrderForm({ onNavigate }) {
  const { products, inventoryLoading, refreshProducts } = useProducts();
  const { user } = useAuth();
  const branchAssignment = useBranchAssignment(user);
  const [branchProducts, setBranchProducts] = useState([]);
  const [branchProductsLoading, setBranchProductsLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [quantities, setQuantities] = useState({});
  const [unitCosts, setUnitCosts] = useState({});
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [partPage, setPartPage] = useState(0);
  const [neededByDate, setNeededByDate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierRegionFilter, setSupplierRegionFilter] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [partCategoryFilter, setPartCategoryFilter] = useState('');

  const catalogSource = branchAssignment.isOwner ? branchProducts : products;

  const catalogParts = useMemo(
    () =>
      (catalogSource || [])
        .filter((p) => String(p.approvalStatus || 'approved') === 'approved')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })),
    [catalogSource]
  );

  useEffect(() => {
    if (!branchAssignment.ready) {
      setBranchProducts([]);
      return undefined;
    }
    if (!branchAssignment.isOwner) {
      setBranchProducts([]);
      return undefined;
    }

    let mounted = true;
    setBranchProductsLoading(true);
    (async () => {
      try {
        const data = await apiJson(`/products${branchQuerySuffix(branchAssignment.branchId)}`);
        if (!mounted) return;
        setBranchProducts(Array.isArray(data.products) ? data.products : []);
      } catch {
        if (mounted) setBranchProducts([]);
      } finally {
        if (mounted) setBranchProductsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [branchAssignment.isOwner, branchAssignment.ready, branchAssignment.branchId]);

  useEffect(() => {
    setSelected(new Set());
    setQuantities({});
    setUnitCosts({});
  }, [branchAssignment.branchId]);

  const partCategories = useMemo(() => {
    const cats = new Set(catalogParts.map((p) => p.category).filter(Boolean));
    return [...cats].sort((a, b) => String(a).localeCompare(String(b)));
  }, [catalogParts]);

  const filteredSuppliers = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (supplierRegionFilter && s.region !== supplierRegionFilter) return false;
      if (!q) return true;
      const haystack = [
        s.name,
        s.phone,
        getRegionName(s.region),
        getDistrictNameResolved(s.region, s.district),
        s.street,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [suppliers, supplierSearch, supplierRegionFilter]);

  const filteredParts = useMemo(() => {
    const q = partSearch.trim().toLowerCase();
    return catalogParts.filter((p) => {
      if (partCategoryFilter && p.category !== partCategoryFilter) return false;
      if (!q) return true;
      const brand = p.brandName || p.brand_name || '';
      const partCode = p.partCode || p.part_code || '';
      const haystack = [p.name, p.category, brand, partCode].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [catalogParts, partSearch, partCategoryFilter]);

  const supplierRegionsInList = useMemo(() => {
    const byId = new Map();
    for (const s of suppliers) {
      if (!s.region || byId.has(s.region)) continue;
      byId.set(s.region, { id: s.region, name: getRegionName(s.region) || s.region });
    }
    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [suppliers]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      next.add(id);
      const p = catalogParts.find((x) => String(x.id) === String(id));
      if (p) {
        const cost = Number(p.purchasingPrice);
        if (Number.isFinite(cost) && cost > 0) {
          setUnitCosts((uc) => (uc[id] != null && uc[id] !== '' ? uc : { ...uc, [id]: String(cost) }));
        }
        setQuantities((q) => (q[id] != null && q[id] !== '' ? q : { ...q, [id]: '1' }));
      }
      return next;
    });
  };

  const handleQuantity = (id, value) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  };

  const handleUnitCost = (id, value) => {
    setUnitCosts((prev) => ({ ...prev, [id]: value }));
  };

  useEffect(() => {
    let mounted = true;
    setSuppliersLoading(true);
    (async () => {
      try {
        const data = await apiJson('/suppliers');
        if (!mounted) return;
        setSuppliers(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!mounted) return;
        setSuppliers([]);
      } finally {
        if (mounted) setSuppliersLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setPartPage(0);
  }, [partSearch, partCategoryFilter]);

  const estimatedGrandTotal = useMemo(() => {
    return [...selected].reduce((sum, id) => {
      const qty = Math.max(1, parseInt(String(quantities[id]), 10) || 0);
      const unit = Math.max(0, Number(unitCosts[id]) || 0);
      return sum + qty * unit;
    }, 0);
  }, [selected, quantities, unitCosts]);

  const composedNotes = () => {
    const lines = ['Spare parts purchase order from catalog'];
    if (neededByDate) lines.push(`Needed by: ${neededByDate}`);
    if (paymentTerms.trim()) lines.push(`Payment terms: ${paymentTerms.trim()}`);
    if (paymentDueDate) lines.push(`Payment due: ${paymentDueDate}`);
    lines.push(`Priority: ${priority}`);
    lines.push(`Estimated total (TZS): ${Math.round(estimatedGrandTotal).toLocaleString()}`);
    return lines.join(' | ');
  };

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  const handleSuccessOk = () => {
    setSuccess(false);
    go('orders-tracking');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    if (!branchAssignment.ready) {
      setError('Chagua tawi ambapo mzigo huu utaingia.');
      setSubmitting(false);
      return;
    }

    if (!supplierId) {
      setError('Select a supplier.');
      setSubmitting(false);
      return;
    }

    const ids = [...selected];
    if (ids.length === 0) {
      setError('Select at least one spare part.');
      setSubmitting(false);
      return;
    }

    const lines = [];
    for (const id of ids) {
      const p = catalogParts.find((x) => String(x.id) === String(id));
      if (!p) continue;
      const raw = quantities[id];
      const qty = parseInt(String(raw), 10);
      if (!Number.isFinite(qty) || qty < 1) {
        setError(`Enter a valid order quantity for "${p.name}".`);
        setSubmitting(false);
        return;
      }
      const onHand = Number(p.quantity) || 0;
      const unit = Number(unitCosts[id]);
      if (!Number.isFinite(unit) || unit <= 0) {
        setError(`Enter purchase price (TZS) for "${p.name}".`);
        setSubmitting(false);
        return;
      }
      lines.push({
        productId: p.id,
        product_name: p.name,
        category: p.category || '',
        quantity: qty,
        estimatedUnitCostTzs: unit,
        reason: `Part order (on hand: ${onHand})`,
      });
    }

    if (lines.length === 0) {
      setError('Nothing to submit.');
      setSubmitting(false);
      return;
    }

    const chosenSupplier = suppliers.find((s) => String(s.id) === String(supplierId));

    try {
      await apiJson('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: lines,
          supplierId: Number(supplierId),
          supplierName: chosenSupplier?.name || undefined,
          supplierPhone: chosenSupplier?.phone || undefined,
          neededByDate: neededByDate || undefined,
          priority,
          paymentTerms: paymentTerms.trim() || undefined,
          paymentDueDate: paymentDueDate || undefined,
          notes: composedNotes(),
          branchId: Number(branchAssignment.branchId),
        }),
      });
      setSuccess(true);
      setSelected(new Set());
      setQuantities({});
      setUnitCosts({});
      setSupplierId('');
      setNeededByDate('');
      setPriority('normal');
      setPaymentTerms('');
      setPaymentDueDate('');
      await refreshProducts();
    } catch (err) {
      setError(err.body?.message || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const hasCachedParts = catalogParts.length > 0;
  const waitingForBootstrap = suppliersLoading || branchProductsLoading || (inventoryLoading && !hasCachedParts);

  const partPageCount = Math.max(1, Math.ceil(filteredParts.length / PART_PAGE_SIZE));
  const safePartPage = Math.min(partPage, partPageCount - 1);
  const pagedParts = filteredParts.slice(
    safePartPage * PART_PAGE_SIZE,
    safePartPage * PART_PAGE_SIZE + PART_PAGE_SIZE
  );

  if (waitingForBootstrap) {
    return (
      <div className="low-stock-form factory-order factory-order--loading" aria-busy="true">
        <div className="factory-order__skeleton factory-order__skeleton--supplier" />
        <div className="factory-order__skeleton factory-order__skeleton--details" />
        <div className="factory-order__skeleton factory-order__skeleton--table" />
        <p className="factory-order__loading-text">Loading order form…</p>
      </div>
    );
  }

  const selectedSupplier = suppliers.find((s) => String(s.id) === String(supplierId));

  if (!suppliersLoading && suppliers.length === 0) {
    return (
      <div className="low-stock-form low-stock-form--empty">
        <h3 className="low-stock-title">Ongeza suppliers kwanza</h3>
        <p className="low-stock-empty-text">
          Sajili wasambazaji wako wa spare parts kabla ya kuagiza mzigo. Nenda Suppliers chini ya Manunuzi.
        </p>
        <button type="button" className="low-stock-submit" style={{ marginTop: 16 }} onClick={() => go('auto-suppliers')}>
          + Add supplier
        </button>
      </div>
    );
  }

  if (!inventoryLoading && catalogParts.length === 0) {
    return (
      <div className="low-stock-form low-stock-form--empty">
        <h3 className="low-stock-title">Sajili spare parts kwanza</h3>
        <p className="low-stock-empty-text">
          Ongeza spare parts kwenye catalog kabla ya kuagiza kutoka kwa supplier. Stock itaongezeka ukishapokea mzigo.
        </p>
        <button type="button" className="low-stock-submit" style={{ marginTop: 16 }} onClick={() => go('auto-products-record')}>
          + Record spare part
        </button>
      </div>
    );
  }

  const hasSupplierFilters = Boolean(supplierSearch.trim() || supplierRegionFilter);
  const hasPartFilters = Boolean(partSearch.trim() || partCategoryFilter);

  return (
    <form className="low-stock-form factory-order" onSubmit={handleSubmit}>
      <section className="factory-order__block">
        <BranchAssignmentField
          user={user}
          assignment={branchAssignment}
          label="Tawi (branch)"
          hint="Agizo hili na mzigo utakaoingia utaandikwa kwa tawi hili."
          className="factory-order__field factory-order__field--full"
        />
      </section>

      <section className="factory-order__block factory-order__block--supplier">
        <div className="factory-order__block-head">
          <span className="factory-order__block-icon factory-order__block-icon--supplier" aria-hidden>
            <LuTruck size={16} />
          </span>
          <div>
            <h3 className="factory-order__block-title">Chagua supplier</h3>
            <p className="factory-order__block-hint">Tafuta au chuja, kisha chagua kutoka kwenye wasambazaji uliowahifadhi.</p>
          </div>
        </div>

        <div className="factory-order__toolbar" style={{ border: 'none', padding: '0 0 0.75rem', background: 'transparent' }}>
          <div className="factory-order__search-wrap">
            <LuSearch className="factory-order__search-icon" size={16} aria-hidden />
            <input
              type="search"
              className="factory-order__search"
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              placeholder="Search supplier name, phone, location…"
              aria-label="Search suppliers"
            />
          </div>
          <select
            className="factory-order__filter-select"
            value={supplierRegionFilter}
            onChange={(e) => setSupplierRegionFilter(e.target.value)}
            aria-label="Filter suppliers by region"
          >
            <option value="">All regions</option>
            {supplierRegionsInList.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {hasSupplierFilters ? (
            <button
              type="button"
              className="factory-order__clear"
              onClick={() => {
                setSupplierSearch('');
                setSupplierRegionFilter('');
              }}
            >
              <LuX size={14} aria-hidden /> Clear
            </button>
          ) : null}
        </div>

        <div className="factory-order__fields factory-order__fields--2">
          <label className="factory-order__field factory-order__field--full">
            <span className="factory-order__label">Supplier *</span>
            <select
              className="factory-order__control factory-order__control--select"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              required
            >
              <option value="">
                {filteredSuppliers.length === 0 ? 'No suppliers match filters' : 'Select supplier'}
              </option>
              {filteredSuppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.phone ? ` — ${s.phone}` : ''}
                  {getRegionName(s.region) ? ` (${getRegionName(s.region)})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        {selectedSupplier ? (
          <div className="factory-order__supplier-card">
            <div className="factory-order__supplier-meta">
              <span className="factory-order__supplier-meta-label">Contact</span>
              <span className="factory-order__supplier-meta-value">
                {selectedSupplier.phone || 'No phone saved'}
              </span>
            </div>
            <div className="factory-order__supplier-meta">
              <span className="factory-order__supplier-meta-label">Location</span>
              <span className="factory-order__supplier-meta-value">
                <LuMapPin size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} aria-hidden />
                {[
                  getRegionName(selectedSupplier.region),
                  getDistrictNameResolved(selectedSupplier.region, selectedSupplier.district),
                  selectedSupplier.street?.trim(),
                ]
                  .filter(Boolean)
                  .join(' · ') || 'No location saved'}
              </span>
            </div>
          </div>
        ) : null}
      </section>

      <section className="factory-order__block">
        <div className="factory-order__block-head">
          <span className="factory-order__block-icon" aria-hidden>
            <LuPackage size={16} />
          </span>
          <div>
            <h3 className="factory-order__block-title">Order details</h3>
            <p className="factory-order__block-hint">Optional delivery and payment info.</p>
          </div>
        </div>
        <div className="factory-order__fields factory-order__fields--2">
          <label className="factory-order__field">
            <span className="factory-order__label">Needed by</span>
            <input
              type="date"
              className="factory-order__control"
              value={neededByDate}
              onChange={(e) => setNeededByDate(e.target.value)}
            />
          </label>
          <label className="factory-order__field">
            <span className="factory-order__label">Priority</span>
            <select
              className="factory-order__control factory-order__control--select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label className="factory-order__field">
            <span className="factory-order__label">Payment terms</span>
            <input
              type="text"
              className="factory-order__control"
              placeholder="e.g. 30 days"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
            />
          </label>
          <label className="factory-order__field">
            <span className="factory-order__label">Payment due</span>
            <input
              type="date"
              className="factory-order__control"
              value={paymentDueDate}
              onChange={(e) => setPaymentDueDate(e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="factory-order__block factory-order__block--materials">
        <div className="factory-order__toolbar">
          <div className="factory-order__search-wrap">
            <LuSearch className="factory-order__search-icon" size={16} aria-hidden />
            <input
              type="search"
              className="factory-order__search"
              value={partSearch}
              onChange={(e) => setPartSearch(e.target.value)}
              placeholder="Search spare parts…"
              aria-label="Search spare parts"
            />
          </div>
          <select
            className="factory-order__filter-select"
            value={partCategoryFilter}
            onChange={(e) => setPartCategoryFilter(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {partCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {hasPartFilters ? (
            <button
              type="button"
              className="factory-order__clear"
              onClick={() => {
                setPartSearch('');
                setPartCategoryFilter('');
              }}
            >
              <LuX size={14} aria-hidden /> Clear
            </button>
          ) : null}
          <span className="factory-order__count">
            {selected.size} selected · {filteredParts.length} shown
          </span>
        </div>

        <div className="low-stock-table-wrap" style={{ border: 'none', borderRadius: 0, marginBottom: 0 }}>
          <table className="low-stock-table">
            <thead>
              <tr>
                <th className="low-stock-th-check" scope="col">
                  Select
                </th>
                <th scope="col">Spare part</th>
                <th scope="col">Category</th>
                <th scope="col">Brand</th>
                <th scope="col">On hand</th>
                <th scope="col">Purchase price (TZS)</th>
                <th scope="col">Order qty</th>
                <th scope="col">Line total</th>
              </tr>
            </thead>
            <tbody>
              {filteredParts.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: '1.5rem' }}>
                    No spare parts match your search or filter.
                  </td>
                </tr>
              ) : (
                pagedParts.map((item) => {
                  const id = item.id;
                  const checked = selected.has(id);
                  const onHand = Number(item.quantity) || 0;
                  const brand = item.brandName || item.brand_name || '—';
                  return (
                    <tr key={id} className={checked ? 'low-stock-row low-stock-row--selected' : 'low-stock-row'}>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(id)}
                          aria-label={`Select ${item.name}`}
                        />
                      </td>
                      <td className="low-stock-name">{item.name}</td>
                      <td className="low-stock-muted">{item.category || '—'}</td>
                      <td className="low-stock-muted">{brand}</td>
                      <td>
                        <span className={onHand === 0 ? 'low-stock-alert' : ''}>{onHand}</span>
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          className="low-stock-qty-input"
                          value={unitCosts[id] ?? ''}
                          onChange={(e) => handleUnitCost(id, e.target.value)}
                          disabled={!checked}
                          aria-label={`Unit cost for ${item.name}`}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          className="low-stock-qty-input"
                          value={quantities[id] ?? ''}
                          onChange={(e) => handleQuantity(id, e.target.value)}
                          disabled={!checked}
                          aria-label={`Order quantity for ${item.name}`}
                        />
                      </td>
                      <td className="low-stock-muted">
                        {checked
                          ? Math.round(
                              Math.max(1, parseInt(String(quantities[id]), 10) || 0) *
                                Math.max(0, Number(unitCosts[id]) || 0)
                            ).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filteredParts.length > PART_PAGE_SIZE ? (
          <div className="factory-order__pager">
            <button
              type="button"
              className="factory-order__pager-btn"
              disabled={safePartPage <= 0}
              onClick={() => setPartPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span className="factory-order__pager-meta">
              Page {safePartPage + 1} of {partPageCount}
              {' · '}
              {filteredParts.length} parts
            </span>
            <button
              type="button"
              className="factory-order__pager-btn"
              disabled={safePartPage >= partPageCount - 1}
              onClick={() => setPartPage((p) => Math.min(partPageCount - 1, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      <div className="factory-order__footer">
        <div className="factory-order__total">
          Estimated total: <span>TZS {Math.round(estimatedGrandTotal).toLocaleString()}</span>
        </div>
        <button
          type="submit"
          className="factory-order__submit"
          disabled={submitting || selected.size === 0 || !supplierId || !branchAssignment.ready}
        >
          {submitting ? 'Submitting…' : 'Submit purchase order'}
        </button>
      </div>

      {error ? (
        <div className="low-stock-alert-box" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="factory-order-success-backdrop" role="presentation" onClick={handleSuccessOk}>
          <div
            className="factory-order-success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auto-order-success-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="factory-order-success-icon" aria-hidden>
              <LuCheck size={28} />
            </div>
            <h3 id="auto-order-success-title" className="factory-order-success-title">
              Order imesubmit vizuri!
            </h3>
            <p className="factory-order-success-text">
              Purchase order yako imetumwa. Owner ataikagua na kuikubali — stock itaongezeka ukishapokea mzigo.
            </p>
            <button type="button" className="factory-order-success-btn" onClick={handleSuccessOk} autoFocus>
              Sawa — nenda Pokea na fuatilia
            </button>
          </div>
        </div>
      ) : null}
    </form>
  );
}
